/**
 * PUBLIC DEMO layout (data-dashboard.scaleadvisors.com/demo). A stripped, read-
 * only version of AppLayout: no login, no account switcher, no Customize / sync
 * controls, no admin nav. Branded "Vital Roots — DEMO". Reuses the same page
 * components, fed by the DemoApiClientProvider (read-only Vital Roots data).
 */

import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  BarChart3, Megaphone, Package, Users, Repeat, RefreshCw, Wrench, Landmark,
  PanelLeftClose, PanelLeftOpen, type LucideIcon,
} from "lucide-react";
import { ThemeToggle } from "../lib/theme.js";
import { DrillProvider } from "../lib/drill.js";
import { ToastProvider } from "../lib/toast.js";
import { PeriodProvider, usePeriod, PERIODS } from "../lib/period.js";
import { Segmented } from "../components/Segmented.js";
import { HideProvider } from "../lib/hide.js";
import { CardApprovalProvider } from "../lib/card-approval.js";
import { useCardSpotlight } from "../lib/spotlight.js";
import { ErrorBoundary } from "../components/ErrorBoundary.js";
import { DisplayOptsProvider } from "../lib/display-opts.js";
import { PageFiltersProvider, PageFilterRail, pageHasProductFilter } from "../lib/page-filters.js";
import { BREAK, useViewportWidth } from "../lib/use-viewport.js";

interface NavItem { label: string; to: string; icon: LucideIcon }
interface NavGroup { label: string | null; items: NavItem[] }

const NAV_GROUPS: NavGroup[] = [
  { label: null, items: [{ label: "Exec", to: "/demo/dashboard", icon: BarChart3 }] },
  { label: "Marketing & Sales", items: [
    { label: "Marketing", to: "/demo/marketing", icon: Megaphone },
    { label: "Products", to: "/demo/products", icon: Package },
  ] },
  { label: "Customers", items: [
    { label: "Customer LTV", to: "/demo/cohorts", icon: Users },
    { label: "Retention", to: "/demo/retention", icon: Repeat },
    { label: "Subscriptions", to: "/demo/subscriptions", icon: RefreshCw },
  ] },
  { label: "Operations", items: [
    { label: "Operations", to: "/demo/operations", icon: Wrench },
    { label: "Financials", to: "/demo/financials", icon: Landmark },
  ] },
];

const TITLES: Record<string, string> = {
  "/demo/dashboard": "Exec", "/demo/marketing": "Marketing", "/demo/products": "Products",
  "/demo/cohorts": "Customer LTV", "/demo/retention": "Retention",
  "/demo/subscriptions": "Subscriptions", "/demo/operations": "Operations",
  "/demo/financials": "Financials",
};

function PeriodBar(): React.ReactElement {
  const period = usePeriod();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <Segmented ariaLabel="Time period" options={PERIODS.map((p) => ({ value: p.value, label: p.short }))} value={(period.key === "custom" ? "" : period.key) as (typeof PERIODS)[number]["value"]} onChange={period.setKey} />
      <span className="dd-num" style={{ color: "var(--text-faint)", fontSize: 12 }}>{period.rangeHint}</span>
    </div>
  );
}

export default function DemoLayout(): React.ReactElement {
  const location = useLocation();
  const width = useViewportWidth();
  const barRef = useRef<HTMLDivElement>(null);
  // "See it live →" deep-links land here with ?spotlight=<renderId> — scroll to
  // and flash that card. Same behaviour as the real app.
  useCardSpotlight();

  // EMBED MODE (?embed=1): the operator's Agency › Cards page iframes a single
  // card via /demo/<page>?spotlight=<id>&embed=1. Strip the nav chrome (sidebar,
  // period switcher, product rail) so the framed content is just the card — but
  // KEEP a "sample data · read-only demo" honesty marker so a preview is never
  // mistaken for a real client's numbers. The ?spotlight= scroll+flash still runs
  // (useCardSpotlight above), so the frame lands on the exact card.
  const embed = new URLSearchParams(location.search).get("embed") === "1";
  const onboardingDemo = new URLSearchParams(location.search).get("onboarding") === "1";

  // Collapse the left nav to a slim icon rail (founder 2026-06-28). Persisted.
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem("dd-demo-nav-collapsed") === "1"; } catch { return false; }
  });
  const compactNav = navCollapsed || width < BREAK.nav;
  const toggleNav = (): void => setNavCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem("dd-demo-nav-collapsed", next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });

  useEffect(() => {
    document.title = `${TITLES[location.pathname] ?? "Demo"} · Profit Playground`;
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  const navLinkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
    ...styles.navItem,
    ...(navCollapsed ? { justifyContent: "center", padding: "9px 0" } : {}),
    ...(isActive ? styles.navItemActive : {}),
  });
  const navIcon = navCollapsed ? iconStyleCollapsed : iconStyle;

  return (
    // CardApprovalProvider feeds the rename overrides (nameOverrides) so the demo
    // shows the operator's renamed titles too. The hidden set is always empty for
    // the demo account (it sees every card), so nothing is hidden here.
    <CardApprovalProvider>
    <PeriodProvider defaultKey="365d" storageKey="dd-demo-period">
    <DisplayOptsProvider>
    <ToastProvider>
    <DrillProvider>
      <div className="dd-demo-sample" style={{ ...styles.root, ...(embed ? { minHeight: "auto" } : {}) }}>
        {!embed && (
        <aside style={{ ...styles.sidebar, width: compactNav ? 66 : 240, padding: compactNav ? "22px 10px" : "22px 16px" }}>
          <div style={{ display: width < BREAK.nav ? "none" : "flex", justifyContent: compactNav ? "center" : "flex-end", marginBottom: 8 }}>
            <button type="button" onClick={toggleNav} style={styles.navToggle}
              title={navCollapsed ? "Expand menu" : "Collapse menu"} aria-label={navCollapsed ? "Expand menu" : "Collapse menu"}>
              {navCollapsed ? <PanelLeftOpen size={18} strokeWidth={1.8} /> : <PanelLeftClose size={18} strokeWidth={1.8} />}
            </button>
          </div>
          <div style={{ ...styles.brand, marginBottom: compactNav ? 16 : 22 }}>
            <span style={{ ...styles.brandRow, justifyContent: compactNav ? "center" : "flex-start" }}>
              <span style={styles.brandMark} aria-hidden="true" />
              {!compactNav && <strong style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>Demo</strong>}
              {!compactNav && <span style={styles.demoTag}>SAMPLE</span>}
            </span>
            {!compactNav && <div style={styles.subBrand}>Profit Playground | sample data</div>}
          </div>

          <nav style={styles.nav}>
            {NAV_GROUPS.map((group) => (
              <div key={group.label ?? "top"} style={{ marginTop: group.label ? 14 : 0 }}>
                {group.label && !compactNav && <div style={styles.groupLabel}>{group.label}</div>}
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink key={item.to} to={item.to} className="dd-nav-item" style={navLinkStyle} title={compactNav ? item.label : undefined}>
                      <Icon size={17} strokeWidth={1.8} style={navIcon} />
                      {!compactNav && <span style={{ flex: 1 }}>{item.label}</span>}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>

          <div style={{ ...styles.userArea, ...(compactNav ? { flexDirection: "column", gap: 8, padding: "12px 0 4px" } : {}) }}>
            {!compactNav && <span style={styles.poweredBy}>Powered by Scale Advisors</span>}
            <ThemeToggle />
          </div>
        </aside>
        )}

        {(() => { const seg = location.pathname.split("/")[2] || "home"; const pageKey = `demo-${seg}`; const hasRail = pageHasProductFilter(pageKey, true) && !embed; return (
        <PageFiltersProvider key={pageKey} accountId="public-demo" pageKey={pageKey} filtersEnabled={hasRail}>
          <main style={{ ...styles.main, ...(width < BREAK.grid ? { padding: "20px 16px" } : {}), ...(embed ? styles.mainEmbed : hasRail ? { maxWidth: "none" } : {}) }}>
            <HideProvider pageKey={pageKey}>
              {embed ? (
                // Honesty marker kept even with the chrome stripped — a framed card
                // must never read as a real client's numbers.
                <div style={styles.embedHonesty}>
                  <b>SAMPLE DATA.</b> None of these numbers are from your business.
                </div>
              ) : (
                <div ref={barRef} style={styles.stickyBar}>
                  <div style={styles.sampleNotice}>
                    <b>Profit Playground: sample data only.</b> None of these numbers are from your business.{onboardingDemo ? " Your real dashboard will unlock after verification." : ""}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <PeriodBar />
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={styles.demoBadge}>Interactive sample | read-only</span>
                    </div>
                  </div>
                </div>
              )}
              <ErrorBoundary key={location.key}>
                <Outlet />
              </ErrorBoundary>
            </HideProvider>
          </main>
          {hasRail && <PageFilterRail />}
        </PageFiltersProvider>
        ); })()}
      </div>
    </DrillProvider>
    </ToastProvider>
    </DisplayOptsProvider>
    </PeriodProvider>
    </CardApprovalProvider>
  );
}

const iconStyle: React.CSSProperties = { marginRight: 10, flexShrink: 0, verticalAlign: "-3px" };
const iconStyleCollapsed: React.CSSProperties = { flexShrink: 0, verticalAlign: "-3px" };

const styles: Record<string, React.CSSProperties> = {
  navToggle: { border: "none", background: "transparent", color: "var(--text-faint)", cursor: "pointer", padding: 5, display: "flex", alignItems: "center", borderRadius: 7 },
  root: { display: "flex", minHeight: "100vh", background: "var(--bg)", color: "var(--text)", fontFamily: "var(--font-body)" },
  sidebar: { width: 240, background: "var(--surface)", borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", padding: "22px 16px", position: "sticky", top: 0, height: "100vh" },
  brand: { marginBottom: 22 },
  brandRow: { display: "flex", alignItems: "center", gap: 9 },
  brandMark: { width: 18, height: 18, borderRadius: 5, background: "var(--accent)", boxShadow: "0 0 0 3px color-mix(in srgb, var(--accent) 22%, transparent)", display: "inline-block" },
  demoTag: { fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "var(--accent-contrast)", background: "var(--accent)", padding: "2px 6px", borderRadius: 5 },
  subBrand: { fontSize: 12, color: "var(--text-faint)", marginTop: 5, marginLeft: 27 },
  nav: { flex: 1, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" },
  groupLabel: { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--text-faint)", padding: "0 12px", marginBottom: 5 },
  navItem: { display: "flex", alignItems: "center", padding: "8px 12px", borderRadius: "var(--r-sm)", textDecoration: "none", fontSize: 13.5, fontWeight: 500 },
  navItemActive: { background: "color-mix(in srgb, var(--accent) 14%, transparent)", color: "var(--text)", fontWeight: 600 },
  userArea: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 8px 4px", borderTop: "1px solid var(--border)", marginTop: 12 },
  poweredBy: { fontSize: 11.5, color: "var(--text-faint)" },
  main: { flex: 1, minWidth: 0, padding: "30px 38px", maxWidth: 1280 },
  stickyBar: { position: "sticky", top: 0, zIndex: 20, background: "var(--bg)", margin: "-30px -38px 14px", padding: "30px 38px 12px", borderBottom: "1px solid var(--border)" },
  demoBadge: { fontSize: 11.5, fontWeight: 600, color: "var(--text-faint)", border: "1px solid var(--border)", borderRadius: 999, padding: "3px 10px" },
  // Embed mode: no sidebar, tighter padding, no negative-margin sticky bleed.
  mainEmbed: { padding: "14px 20px 30px", maxWidth: "none" },
  embedHonesty: { width: "fit-content", fontSize: 12, color: "var(--text)", border: "2px solid var(--c2)", background: "color-mix(in srgb, var(--c2) 8%, var(--surface))", borderRadius: 8, padding: "7px 11px", marginBottom: 14 },
  sampleNotice: { border: "2px solid var(--c2)", background: "color-mix(in srgb, var(--c2) 8%, var(--surface))", color: "var(--text)", borderRadius: 8, padding: "8px 11px", marginBottom: 10, fontSize: 12.5, lineHeight: 1.4 },
};
